import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BadgeTier from "../components/BadgeTier";
import api, { BACKEND_URL } from "../lib/api";
import { Trophy, Flame, Zap, Crown, Award } from "lucide-react";
import SEO from "../components/SEO";

const TABS = [
  { key: "xp", label: "XP", icon: Zap },
  { key: "reputation", label: "Reputation", icon: Trophy },
  { key: "streak", label: "Streak", icon: Flame },
];

function toAbsolute(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

const RANK_COLORS = ["text-[#FFD700]", "text-slate-300", "text-[#CD7F32]"];

export default function Leaderboard() {
  const [tab, setTab] = useState("xp");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?sort=${tab}&limit=50`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Leaderboard · Top Creators on Nexus"
        description="Live rankings of the top creators on Nexus by XP earned, reputation score and login streak. Discover the best creators to hire."
        path="/leaderboard"
        keywords={["top creators", "creator rankings", "best influencers", "influencer leaderboard", "top rated content creators"]}
      />
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Leaderboard</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
            The top <span className="nexus-gradient-text">creators on Nexus</span>.
          </h1>
          <p className="text-zinc-400 mt-2">Ranked by real work — XP, reputation and streaks.</p>
        </div>

        <div className="flex gap-2" data-testid="leaderboard-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`leaderboard-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors flex items-center gap-2 ${
                  tab === t.key ? "border-nexus-purple bg-nexus-purple/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="glass rounded-2xl overflow-hidden" data-testid="leaderboard-table">
          {loading ? (
            <div className="p-10 text-center text-zinc-500 animate-pulse">Loading rankings…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">No rankings yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map((r, i) => {
                const rank = i + 1;
                return (
                  <Link
                    to={`/creators/${r.user_id}`}
                    key={r.user_id}
                    className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors"
                    data-testid={`leaderboard-row-${rank}`}
                  >
                    <div className={`w-10 text-center font-display font-black text-xl ${RANK_COLORS[i] || "text-zinc-500"}`}>
                      {rank <= 3 ? <Crown className="h-5 w-5 inline mb-1" /> : null} {rank}
                    </div>
                    <div className="h-11 w-11 rounded-full overflow-hidden hairline bg-black/40 shrink-0">
                      {r.avatar_url && <img src={toAbsolute(r.avatar_url)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold truncate">{r.name}</div>
                        <BadgeTier tier={r.badge_tier} size="sm" />
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        Lv {r.level} · {r.level_name} · {r.completed_jobs} jobs
                      </div>
                    </div>
                    <div className="text-right hidden md:block">
                      <div className="text-xs uppercase tracking-widest text-zinc-500">
                        {tab === "xp" ? "XP" : tab === "streak" ? "Streak" : "Reputation"}
                      </div>
                      <div className="font-display font-bold text-xl nexus-gradient-text">
                        {tab === "xp"
                          ? r.xp.toLocaleString()
                          : tab === "streak"
                          ? `${r.current_streak}d`
                          : r.reputation_score.toFixed(0)}
                      </div>
                    </div>
                    <div className="md:hidden text-right">
                      <div className="font-display font-bold nexus-gradient-text">
                        {tab === "xp" ? r.xp : tab === "streak" ? `${r.current_streak}d` : r.reputation_score.toFixed(0)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline">
            <Zap className="h-5 w-5 text-nexus-blue mb-2" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">How XP works</div>
            <p className="text-sm text-zinc-300">Earn XP by joining, verifying, applying, completing hires, and receiving strong reviews. XP is status only — never redeemable.</p>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline">
            <Flame className="h-5 w-5 text-nexus-pink mb-2" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Streaks</div>
            <p className="text-sm text-zinc-300">Log in every day. Every 7-day milestone earns you bonus XP. Miss a day and the streak resets.</p>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline">
            <Award className="h-5 w-5 text-nexus-purple mb-2" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Reputation</div>
            <p className="text-sm text-zinc-300">Weighted from star rating, completion rate and completed jobs. Brands see this on every profile.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
